/**
 * Health sync — reads steps + weight from the device health store
 * (Apple HealthKit on iOS, Health Connect on Android via @capgo/capacitor-health)
 * and pushes them into the existing GLPFlo backend. No-ops in a normal browser.
 *
 * Steps  → POST /stepathon/events/{slug}/log  (source "companion")
 * Weight → POST /wellness/weight/import       (backdated, no streak/coupons)
 */
import { Capacitor } from "@capacitor/core";
import API from "@/lib/api";

const KG_TO_LBS = 2.20462;

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

async function loadHealth() {
  if (!Capacitor?.isNativePlatform?.()) return null;
  try {
    const mod = await import("@capgo/capacitor-health");
    return mod.Health || null;
  } catch {
    return null;
  }
}

export async function isHealthAvailable() {
  const Health = await loadHealth();
  if (!Health) return { available: false, reason: "not-native" };
  try {
    return await Health.isAvailable();
  } catch (e) {
    return { available: false, reason: String(e?.message || e) };
  }
}

/**
 * Pull today's steps + the last 30 days of weight and sync to the backend.
 * @param {string} [stepEventSlug] active step-a-thon event to log steps against.
 * @returns {Promise<{steps:number|null, weightImported:number|null, error?:string}>}
 */
export async function syncHealthData(stepEventSlug) {
  const Health = await loadHealth();
  if (!Health) return { steps: null, weightImported: null, error: "Health sync only works in the installed app." };

  const avail = await Health.isAvailable().catch(() => ({ available: false }));
  if (!avail.available) return { steps: null, weightImported: null, error: "Health data isn't available on this device." };

  await Health.requestAuthorization({ read: ["steps", "weight"], write: [] });

  // Resolve the current step-a-thon event to log steps against, if any.
  if (!stepEventSlug) {
    try {
      const { data } = await API.get("/stepathon/events", { params: { status: "active" } });
      const list = Array.isArray(data) ? data : (data?.events || []);
      stepEventSlug = list[0]?.slug;
    } catch { /* no active event — steps just won't be logged */ }
  }

  const now = new Date();
  const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  let steps = null;
  try {
    const { samples } = await Health.queryAggregated({
      dataType: "steps",
      startDate: startOfDay.toISOString(),
      endDate: now.toISOString(),
      bucket: "day",
      aggregation: "sum",
    });
    steps = Math.round((samples || []).reduce((sum, s) => sum + (Number(s.value) || 0), 0));
    if (steps > 0 && stepEventSlug) {
      await API.post(`/stepathon/events/${stepEventSlug}/log`, { steps, source: "companion" });
    }
  } catch { /* steps optional */ }

  let weightImported = null;
  try {
    const { samples } = await Health.readSamples({
      dataType: "weight",
      startDate: thirtyDaysAgo.toISOString(),
      endDate: now.toISOString(),
      limit: 200,
    });
    // One weigh-in per calendar day (latest sample wins).
    const byDay = {};
    (samples || []).forEach((s) => {
      const lbs = (s.unit === "kilogram" || s.unit == null) ? Number(s.value) * KG_TO_LBS : Number(s.value);
      if (Number.isFinite(lbs) && lbs > 20 && lbs < 1500) byDay[iso(new Date(s.startDate))] = Math.round(lbs * 10) / 10;
    });
    const entries = Object.entries(byDay).map(([date, weight_lbs]) => ({ date, weight_lbs }));
    if (entries.length) {
      const { data } = await API.post("/wellness/weight/import", { entries, source: "apple_health" });
      weightImported = data?.imported ?? entries.length;
    } else {
      weightImported = 0;
    }
  } catch { /* weight optional */ }

  return { steps, weightImported };
}
